import { c as _c } from "react/compiler-runtime";
import type { ValidationSeverity } from '@onefootprint/request-types/dashboard';
import type { FieldKeyError } from '../store/field-key';
import { visibleValidations } from '../store/instruction-validation';
import { useBuilder } from '../store/provider';
import type { State } from '../store/reducer';
import { INPUT_NODE_ID, OUTPUTS_NODE_ID } from '../store/types';
import { keyErrors } from '../store/utils';
type Issue = {
  severity: 'error' | 'warning';
  /** A node id, or INPUT_NODE_ID / OUTPUTS_NODE_ID for the sections that aren't nodes. */
  nodeId: string;
  target: string;
  blockTarget?: string;
  key?: string;
  keyError?: FieldKeyError;
  message?: string;
};
const SEVERITY: Record<ValidationSeverity, Issue['severity'] | null> = {
  immediate_error: 'error',
  error: 'error',
  warning: 'warning',
  info: null
};
const validationIssues = (state: State, reveal?: boolean): Issue[] => {
  const {
    nodes
  } = state.bundle;
  const fromKeys = keyErrors(state.bundle).map(({
    nodeId,
    target,
    key,
    error
  }): Issue => {
    return {
      severity: 'error',
      nodeId,
      target,
      key,
      keyError: error
    };
  });
  // Graph-level findings (id undefined) render in the final-output card.
  const fromServer = visibleValidations(state, reveal).flatMap(validation => {
    const nodeId = validation.id ?? OUTPUTS_NODE_ID;
    const node = nodes.find(candidate => candidate.id === nodeId);
    return validation.details.flatMap((detail): Issue[] => {
      const severity = SEVERITY[detail.severity];
      if (!severity) return [];
      // A revealed instruction block scrolls itself into view; a section anchor would pull the page back up.
      const target = detail.blockTarget || !node ? nodeId : `${node.kind}-${node.id}`;
      return [{
        severity,
        nodeId,
        target,
        blockTarget: detail.blockTarget,
        message: detail.message
      }];
    });
  });
  // Key errors stay ahead within a section: a node holding one never saves, so its server findings are stale.
  const order = [INPUT_NODE_ID, ...nodes.map(node => node.id), OUTPUTS_NODE_ID];
  return [...fromKeys, ...fromServer].sort((a, b) => order.indexOf(a.nodeId) - order.indexOf(b.nodeId));
};
const useValidationSummary = () => {
  const $ = _c(6);
  const {
    state
  } = useBuilder();
  let t0;
  let t1;
  if ($[0] !== state) {
    const issues = validationIssues(state);
    t0 = issues;
    t1 = issues.filter(_temp);
    $[0] = state;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  let t2;
  if ($[3] !== t0 || $[4] !== t1.length) {
    t2 = {
      issues: t0,
      errorCount: t1.length
    };
    $[3] = t0;
    $[4] = t1.length;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  return t2;
};
export type { Issue };
export { validationIssues };
export default useValidationSummary;
function _temp(issue) {
  return issue.severity === "error";
}